/* =============================================================================
 * Arabic content shared by public pages (alphabet test + memo cards).
 *
 * This file is intentionally standalone: public pages must work
 * without an account and without API calls, even if the backend is
 * temporarily unavailable. If you want to connect your real data later,
 * simply replace the imports in the two pages.
 * ========================================================================== */

const ZWJ = "\u200D";

/** Contextual forms calculated with the Unicode joiner (ZWJ).
 *  The six letters that do not connect to the left (ا د ذ ر ز و) naturally
 *  remain detached: this is the correct behavior. */
export function letterForms(char) {
  return {
    isolated: char,
    initial: char + ZWJ,
    medial: ZWJ + char + ZWJ,
    final: ZWJ + char,
  };
}

/** The six letters that never connect to the following letter. */
export const NON_CONNECTORS = ["ا", "د", "ذ", "ر", "ز", "و"];

export const ALPHABET_GROUPS = [
  {
    id: "g1",
    label: "Alif to Tha",
    hint: "Same tooth, the dots do everything.",
    letters: [
      {
        char: "ا",
        name: "alif",
        sound: "â",
        word: "أَسَد",
        wordTranslit: "asad",
        wordEn: "lion",
        note: "A simple vertical line. Never connects to the following letter.",
      },
      {
        char: "ب",
        name: "ba",
        sound: "b",
        word: "بَيت",
        wordTranslit: "bayt",
        wordEn: "house",
        note: "One dot underneath.",
      },
      {
        char: "ت",
        name: "ta",
        sound: "t",
        word: "تُفّاح",
        wordTranslit: "tuffâh",
        wordEn: "apple",
        note: "Two dots above.",
      },
      {
        char: "ث",
        name: "tha",
        sound: "th",
        word: "ثَوب",
        wordTranslit: "thawb",
        wordEn: "clothes",
        note: "Three dots above, like the English 'th' in think.",
      },
    ],
  },
  {
    id: "g2",
    label: "Jim, Ha, Kha",
    hint: "Same rounded body; dot below, none, or above.",
    letters: [
      {
        char: "ج",
        name: "jim",
        sound: "j",
        word: "جَمَل",
        wordTranslit: "jamal",
        wordEn: "camel",
        note: "One dot inside, at the bottom.",
      },
      {
        char: "ح",
        name: "ha",
        sound: "breathy h",
        word: "حِصان",
        wordTranslit: "hisân",
        wordEn: "horse",
        note: "No dot. A breath coming from the back of the throat.",
      },
      {
        char: "خ",
        name: "kha",
        sound: "kh",
        word: "خُبز",
        wordTranslit: "khubz",
        wordEn: "bread",
        note: "One dot above. Raspy, like the Spanish jota.",
      },
    ],
  },
  {
    id: "g3",
    label: "Dal to Zay",
    hint: "Four letters that do not connect to the left.",
    letters: [
      {
        char: "د",
        name: "dal",
        sound: "d",
        word: "دار",
        wordTranslit: "dâr",
        wordEn: "home",
        note: "Small hook, without a dot.",
      },
      {
        char: "ذ",
        name: "dhal",
        sound: "dh",
        word: "ذَهَب",
        wordTranslit: "dhahab",
        wordEn: "gold",
        note: "Dal with a dot: the voiced 'th' of this.",
      },
      {
        char: "ر",
        name: "ra",
        sound: "rolled r",
        word: "رَجُل",
        wordTranslit: "rajul",
        wordEn: "man",
        note: "Descends below the line, without a dot.",
      },
      {
        char: "ز",
        name: "zay",
        sound: "z",
        word: "زَيت",
        wordTranslit: "zayt",
        wordEn: "oil",
        note: "Ra with a dot above.",
      },
    ],
  },
  {
    id: "g4",
    label: "Sin to Dad",
    hint: "The teeth, then their emphatic versions.",
    letters: [
      {
        char: "س",
        name: "sin",
        sound: "s",
        word: "سَمَك",
        wordTranslit: "samak",
        wordEn: "fish",
        note: "Three teeth, without a dot.",
      },
      {
        char: "ش",
        name: "shin",
        sound: "sh",
        word: "شَمس",
        wordTranslit: "shams",
        wordEn: "sun",
        note: "Three teeth and three dots.",
      },
      {
        char: "ص",
        name: "sad",
        sound: "emphatic s",
        word: "صَديق",
        wordTranslit: "sadîq",
        wordEn: "friend",
        note: "Closed loop. The sound is deeper, the mouth more open.",
      },
      {
        char: "ض",
        name: "dad",
        sound: "emphatic d",
        word: "ضَوء",
        wordTranslit: "daw'",
        wordEn: "light",
        note: "Sad with a dot. The letter that gave Arabic its nickname.",
      },
    ],
  },
  {
    id: "g5",
    label: "Ta to Ghayn",
    hint: "Two emphatics, then the two throat sounds.",
    letters: [
      {
        char: "ط",
        name: "emphatic ta",
        sound: "deep t",
        word: "طالِب",
        wordTranslit: "tâlib",
        wordEn: "student",
        note: "A loop and a vertical bar.",
      },
      {
        char: "ظ",
        name: "emphatic dha",
        sound: "deep dh",
        word: "ظِلّ",
        wordTranslit: "zill",
        wordEn: "shadow",
        note: "Emphatic Ta with a dot.",
      },
      {
        char: "ع",
        name: "ayn",
        sound: "ʿa",
        word: "عَين",
        wordTranslit: "ʿayn",
        wordEn: "eye",
        note: "Throat constriction, without equivalent in English.",
      },
      {
        char: "غ",
        name: "ghayn",
        sound: "guttural r",
        word: "غُرفة",
        wordTranslit: "ghurfa",
        wordEn: "room",
        note: "Ayn with a dot: close to the French 'r' in Paris.",
      },
    ],
  },
  {
    id: "g6",
    label: "Fa to Lam",
    hint: "Two pairs often confused.",
    letters: [
      {
        char: "ف",
        name: "fa",
        sound: "f",
        word: "فيل",
        wordTranslit: "fîl",
        wordEn: "elephant",
        note: "A loop, one dot above.",
      },
      {
        char: "ق",
        name: "qaf",
        sound: "q",
        word: "قَلَم",
        wordTranslit: "qalam",
        wordEn: "pen",
        note: "Two dots. The 'k' sound is formed at the very back of the palate.",
      },
      {
        char: "ك",
        name: "kaf",
        sound: "k",
        word: "كِتاب",
        wordTranslit: "kitâb",
        wordEn: "book",
        note: "The regular 'k', formed at the front of the mouth.",
      },
      {
        char: "ل",
        name: "lam",
        sound: "l",
        word: "لَيل",
        wordTranslit: "layl",
        wordEn: "night",
        note: "Tall stroke that descends below the line.",
      },
    ],
  },
  {
    id: "g7",
    label: "Mim to Ya",
    hint: "The last five, two of which also serve as long vowels.",
    letters: [
      {
        char: "م",
        name: "mim",
        sound: "m",
        word: "ماء",
        wordTranslit: "mâ'",
        wordEn: "water",
        note: "Small loop with a tail.",
      },
      {
        char: "ن",
        name: "nun",
        sound: "n",
        word: "نار",
        wordTranslit: "nâr",
        wordEn: "fire",
        note: "Deep bowl, one dot above.",
      },
      {
        char: "ه",
        name: "ha",
        sound: "light h",
        word: "هَواء",
        wordTranslit: "hawâ'",
        wordEn: "air",
        note: "Changes shape significantly depending on its position.",
      },
      {
        char: "و",
        name: "waw",
        sound: "w / oo",
        word: "وَرد",
        wordTranslit: "ward",
        wordEn: "flowers",
        note: "Consonant 'w', or long vowel 'oo'.",
      },
      {
        char: "ي",
        name: "ya",
        sound: "y / ee",
        word: "يَد",
        wordTranslit: "yad",
        wordEn: "hand",
        note: "Consonant 'y', or long vowel 'ee'. Two dots underneath.",
      },
    ],
  },
];

export const ALL_LETTERS = ALPHABET_GROUPS.flatMap((g) =>
  g.letters.map((l) => ({ ...l, groupId: g.id, groupLabel: g.label })),
);

/* -------------------------------------------------------------------------- */
/*  Memo Cards                                                                */
/* -------------------------------------------------------------------------- */

export const VOWEL_SIGNS = [
  { sign: "بَ", name: "fatha", read: "short a", note: "Small stroke above." },
  { sign: "بِ", name: "kasra", read: "short i", note: "Small stroke below." },
  { sign: "بُ", name: "damma", read: "short u", note: "Small loop above." },
  {
    sign: "بْ",
    name: "sukun",
    read: "no vowel",
    note: "Small circle: the consonant is bare.",
  },
  {
    sign: "بّ",
    name: "shadda",
    read: "doubled consonant",
    note: "Emphasis is placed twice on the letter.",
  },
  {
    sign: "بً",
    name: "tanwin fath",
    read: "an",
    note: "Marks the indefinite, in the accusative.",
  },
  {
    sign: "بٍ",
    name: "tanwin kasr",
    read: "in",
    note: "Marks the indefinite, in the genitive.",
  },
  {
    sign: "بٌ",
    name: "tanwin damm",
    read: "un",
    note: "Marks the indefinite, in the nominative.",
  },
  {
    sign: "با",
    name: "alif of prolongation",
    read: "long â",
    note: "Doubles the duration of the 'a'.",
  },
  {
    sign: "بو",
    name: "waw of prolongation",
    read: "long oo",
    note: "Doubles the duration of the 'oo'.",
  },
  {
    sign: "بي",
    name: "ya of prolongation",
    read: "long ee",
    note: "Doubles the duration of the 'ee'.",
  },
  { sign: "ة", name: "ta marbuta", read: "a / at", note: "Feminine ending." },
];

export const PRONOUNS = [
  { ar: "أَنا", tr: "ana", en: "I" },
  { ar: "أَنتَ", tr: "anta", en: "you (masculine)" },
  { ar: "أَنتِ", tr: "anti", en: "you (feminine)" },
  { ar: "هُوَ", tr: "huwa", en: "he" },
  { ar: "هِيَ", tr: "hiya", en: "she" },
  { ar: "نَحنُ", tr: "nahnu", en: "we" },
  { ar: "أَنتُم", tr: "antum", en: "you (plural)" },
  { ar: "هُم", tr: "hum", en: "they" },
];

export const POSSESSIVES = [
  { ar: "كِتابي", tr: "kitâbî", en: "my book" },
  { ar: "كِتابُكَ", tr: "kitâbuka", en: "your book (m.)" },
  { ar: "كِتابُكِ", tr: "kitâbuki", en: "your book (f.)" },
  { ar: "كِتابُهُ", tr: "kitâbuhu", en: "his book" },
  { ar: "كِتابُها", tr: "kitâbuhâ", en: "her book" },
  { ar: "كِتابُنا", tr: "kitâbunâ", en: "our book" },
];

export const PHRASES = [
  {
    ar: "السَّلامُ عَلَيكُم",
    tr: "as-salâmu ʿalaykum",
    en: "hello (peace be upon you)",
  },
  {
    ar: "وَعَلَيكُمُ السَّلام",
    tr: "wa ʿalaykumu s-salâm",
    en: "reply to greeting",
  },
  { ar: "صَباحُ الخَير", tr: "sabâh al-khayr", en: "good morning" },
  { ar: "مَساءُ الخَير", tr: "masâ' al-khayr", en: "good evening" },
  { ar: "كَيفَ حالُك؟", tr: "kayfa hâluk ?", en: "how are you?" },
  { ar: "بِخَير، شُكراً", tr: "bi-khayr, shukran", en: "fine, thank you" },
  { ar: "مِن فَضلِك", tr: "min fadlik", en: "please" },
  { ar: "عَفواً", tr: "ʿafwan", en: "you're welcome / excuse me" },
  { ar: "ما اسمُك؟", tr: "mâ ismuk ?", en: "what is your name?" },
  { ar: "اسمي…", tr: "ismî…", en: "my name is…" },
  { ar: "لا أَفهَم", tr: "lâ afham", en: "I don't understand" },
  { ar: "مَعَ السَّلامة", tr: "maʿa s-salâma", en: "goodbye" },
];

export const NUMBERS = [
  { ar: "صِفر", digit: "٠", tr: "sifr", en: "0" },
  { ar: "واحِد", digit: "١", tr: "wâhid", en: "1" },
  { ar: "اِثنان", digit: "٢", tr: "ithnân", en: "2" },
  { ar: "ثَلاثة", digit: "٣", tr: "thalâtha", en: "3" },
  { ar: "أَربَعة", digit: "٤", tr: "arbaʿa", en: "4" },
  { ar: "خَمسة", digit: "٥", tr: "khamsa", en: "5" },
  { ar: "سِتّة", digit: "٦", tr: "sitta", en: "6" },
  { ar: "سَبعة", digit: "٧", tr: "sabʿa", en: "7" },
  { ar: "ثَمانية", digit: "٨", tr: "thamâniya", en: "8" },
  { ar: "تِسعة", digit: "٩", tr: "tisʿa", en: "9" },
  { ar: "عَشَرة", digit: "١٠", tr: "ʿashara", en: "10" },
];

export const SUN_LETTERS = "ت ث د ذ ر ز س ش ص ض ط ظ ل ن".split(" ");
export const MOON_LETTERS = "ا ب ج ح خ ع غ ف ق ك م ه و ي".split(" ");

export const VERB_KATABA = {
  root: "ك ت ب",
  meaning: "to write",
  past: [
    { p: "أنا", ar: "كَتَبتُ", tr: "katabtu" },
    { p: "أنتَ", ar: "كَتَبتَ", tr: "katabta" },
    { p: "أنتِ", ar: "كَتَبتِ", tr: "katabti" },
    { p: "هو", ar: "كَتَبَ", tr: "kataba" },
    { p: "هي", ar: "كَتَبَت", tr: "katabat" },
    { p: "نحن", ar: "كَتَبنا", tr: "katabnâ" },
  ],
  present: [
    { p: "أنا", ar: "أَكتُبُ", tr: "aktubu" },
    { p: "أنتَ", ar: "تَكتُبُ", tr: "taktubu" },
    { p: "أنتِ", ar: "تَكتُبينَ", tr: "taktubîna" },
    { p: "هو", ar: "يَكتُبُ", tr: "yaktubu" },
    { p: "هي", ar: "تَكتُبُ", tr: "taktubu" },
    { p: "نحن", ar: "نَكتُبُ", tr: "naktubu" },
  ],
};

export const ROOT_PATTERNS = [
  {
    pattern: "فاعِل",
    role: "the one who does",
    ex: "كاتِب",
    tr: "kâtib",
    en: "writer",
  },
  {
    pattern: "مَفعول",
    role: "what is done",
    ex: "مَكتوب",
    tr: "maktûb",
    en: "written, letter",
  },
  {
    pattern: "مَفعَل",
    role: "place of action",
    ex: "مَكتَب",
    tr: "maktab",
    en: "desk / office",
  },
  {
    pattern: "مَفعَلة",
    role: "place, collection",
    ex: "مَكتَبة",
    tr: "maktaba",
    en: "library",
  },
  {
    pattern: "فِعالة",
    role: "activity",
    ex: "كِتابة",
    tr: "kitâba",
    en: "writing",
  },
  {
    pattern: "فِعال",
    role: "concrete object",
    ex: "كِتاب",
    tr: "kitâb",
    en: "book",
  },
];
